import { createI18n, useI18n } from 'vue-i18n'
import { getCurrentInstance, onUnmounted, provide, watch } from 'vue'
import type { App, ComputedRef, InjectionKey, Ref } from 'vue'

type LocaleRef = ComputedRef<string> | Ref<string>

type NuxtI18nApp = App & {
    __VUE_I18N_SYMBOL__?: InjectionKey<unknown>
}

export function useScopedGlobalI18n(locale: LocaleRef) {
    const parentI18n = useI18n({ useScope: 'global' })
    const currentInstance = getCurrentInstance()

    if (currentInstance === null) {
        throw new Error('Scoped i18n must be created during setup.')
    }

    const i18nSymbol = (currentInstance.appContext.app as NuxtI18nApp)
        .__VUE_I18N_SYMBOL__
    if (i18nSymbol === undefined) {
        throw new Error('Scoped i18n requires Nuxt i18n.')
    }

    const scopedI18n = createI18n({
        legacy: false,
        globalInjection: false,
        locale: locale.value,
        fallbackLocale: parentI18n.fallbackLocale.value,
        messages: parentI18n.messages.value
    })

    watch(
        locale,
        nextLocale => {
            // Dynamic route reuse and dynamic dialogs can outlive the exact
            // setup call that created them, so keep their shadow composer in
            // sync with the current agreement language.
            scopedI18n.global.locale.value = nextLocale
        },
        { immediate: true }
    )

    // Descendants still call useI18n({ useScope: 'global' }), but this shadows
    // that "global" composer for only the subtree rooted at the caller.
    provide(i18nSymbol, scopedI18n)

    onUnmounted(() => {
        // This only disposes the shadow composer; the cookie-backed storefront
        // locale remains owned by the normal app-level i18n instance.
        scopedI18n.dispose()
    })

    return scopedI18n.global
}
